import React from "react";
import { withSnackbar } from "notistack";
import { injectIntl, FormattedMessage } from "react-intl";
import DataGrid, {
  Column,
  Editing,
  Export,
  SearchPanel,
  HeaderFilter,
  ColumnChooser,
  ColumnFixing,
  Toolbar,
  Item as ToolbarItem,
  Scrolling,
  Button,
  StateStoring,
  FilterPanel,
  FilterRow
} from "devextreme-react/data-grid";
import SelectBox from "devextreme-react/select-box";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import * as aiSettingsCrud from "../../../../crud/aisettings.crud";
import { Portlet, PortletBody } from "../../../../partials/content/Portlet";
import PortletSpinner from "../../../../partials/layout/PortletSpinner";

class AISettings extends React.Component {
  constructor(props) {
    super(props);
    this.gridRef = React.createRef();

    this.typeOptions = [
      { value: "COMPANY", name: "Companies" },
      { value: "PROJECT", name: "Projects" },
      { value: "CONTRACT", name: "Contracts" }
    ];

    this.state = {
      isBusy: true,
      data: [],
      selectedType: "COMPANY"
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    try {
      this.setState({ isBusy: true });

      const { selectedType } = this.state;
      const { data } =
        await aiSettingsCrud.AISettingsToSelectList(selectedType);

      this.setState({
        data: Array.isArray(data) ? data : [],
        isBusy: false
      });
    } catch (err) {
      this.props.enqueueSnackbar(err.toString(), { variant: "error" });
      this.setState({ isBusy: false });
    }
  };

  handleTypeChange = (e) => {
    this.setState(
      { selectedType: e.value },
      () => this.loadData()
    );
  };

  onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("AISettings");

    exportDataGrid({
      component: e.component,
      worksheet
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "AISettings.xlsx"
        );
      });
    });

    e.cancel = true;
  };

  onRowUpdating = async (e) => {
    const updated = { ...e.oldData, ...e.newData };
    e.cancel = this.SaveAISettings(updated);
  };

  onRowInserting = async (e) => {
    e.cancel = this.SaveAISettings(e.data);
  };

  onRowRemoving = async (e) => {
    this.props.enqueueSnackbar("Delete not implemented in API.", {
      variant: "warning"
    });
    e.cancel = true;
  };

  SaveAISettings = (model) => {
    return new Promise(async (resolve, reject) => {
      try {
        await aiSettingsCrud.SaveAISettings(model);

        this.props.enqueueSnackbar(
          this.props.intl.formatMessage({
            id: "ACTIONS.TOAST.SAVED",
            defaultMessage: "Saved successfully"
          }),
          { variant: "success" }
        );

        await this.loadData();
        resolve(false);
      } catch (error) {
        this.props.enqueueSnackbar(error.toString(), {
          variant: "error"
        });
        reject(error);
      }
    });
  };

  render() {
    if (this.state.isBusy && this.state.data.length === 0) {
      return <PortletSpinner />;
    }

    return (
      <Portlet>
        <PortletBody>

          <div style={{ marginBottom: 20 }}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                margin: 0,
                color: "#48465b"
              }}
            >
              <FormattedMessage
                id="AI_SETTINGS.TITLE"
                defaultMessage="AI Settings"
              />
            </h2>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 15
            }}
          >

            <div style={{ width: 1000 }}>
              <SelectBox
                items={this.typeOptions}
                value={this.state.selectedType}
                displayExpr="name"
                valueExpr="value"
                onValueChanged={this.handleTypeChange}
              />
            </div>
          </div>

          <DataGrid
            ref={this.gridRef}
            height={"67vh"}
            dataSource={this.state.data}
            keyExpr="referenceid"
            hoverStateEnabled={true}
            rowAlternationEnabled={false}
            showBorders={true}
            showColumnLines={true}
            showRowLines={true}
            remoteOperations={false}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnResizingMode="widget"
            onRowUpdating={this.onRowUpdating}
            onRowInserting={this.onRowInserting}
            onRowRemoving={this.onRowRemoving}
            onExporting={this.onExporting}
            loadPanel={{ enabled: false }}
            style={{ width: "100%", height: "100%", maxHeight: "67vh" }}
          >
            <HeaderFilter visible={true} />
            <FilterPanel visible={true} />
            <FilterRow applyFilter="auto" visible={true} />
            <ColumnChooser enabled={true} />
            <ColumnFixing enabled={true} />

            <StateStoring
              enabled={true}
              type="localStorage"
              storageKey={
                "FD-AISettings-" +
                this.state.selectedType +
                "-" +
                process.env.REACT_APP_PRODUCT_KEY +
                process.env.REACT_APP_ENVIRONMENT
              }
            />

            <SearchPanel visible={true} />
            <Export enabled={true} fileName="AISettings" />

            <Editing
              mode="popup"
              allowUpdating={true}
              allowDeleting={false}
              allowAdding={false}
              useIcons={true}
              popup={{
                title: "AI Settings",
                showTitle: true,
                width: 500,
                height: "auto"
              }}
              form={{
              colCount: 1,
              labelLocation: "top",
              items: [
                {
                  itemType: "group",
                  colCount: 2,
                  items: [
                    { dataField: "supportarticles", editorType: "dxSwitch" },
                    { dataField: "reachbackdata", editorType: "dxSwitch" },
                    { dataField: "sentimentanalysis", editorType: "dxSwitch" },
                    { dataField: "aisummarisation", editorType: "dxSwitch" },
                    { dataField: "attachments", editorType: "dxSwitch" },
                    { dataField: "documents", editorType: "dxSwitch" },
                    { dataField: "useruploads", editorType: "dxSwitch" }
                  ]
                }
              ]
            }}
            />

            <Column dataField="type" caption="Type" allowEditing={false} />
            <Column dataField="name" caption="Name" allowEditing={false} />
            <Column dataField="referenceid" caption="Reference ID" allowEditing={false} />
            <Column dataField="supportarticles" caption="Support Articles" dataType="boolean" />
            <Column dataField="reachbackdata" caption="Reachback Data" dataType="boolean" />
            <Column dataField="sentimentanalysis" caption="Sentiment Analysis" dataType="boolean" />
            <Column dataField="aisummarisation" caption="AI Summarisation" dataType="boolean" />
            <Column dataField="attachments" caption="Attachments" dataType="boolean" />
            <Column dataField="documents" caption="Documents" dataType="boolean" />
            <Column dataField="useruploads" caption="User Uploads" dataType="boolean" />

            <Column type="buttons" width={60}>
              <Button
                icon="edit"
                hint="Edit"
                onClick={(e) =>
                  this.gridRef.current.instance().editRow(e.row.rowIndex)
                }
              />
            </Column>

            <Scrolling
              mode="virtual"
              showScrollbar={true}
              useNative={true}
              columnRenderingMode="virtual"
              rowRenderingMode="virtual"
            />

            <Toolbar>
              <ToolbarItem
                location="after"
                locateInMenu="always"
                widget="dxButton"
                options={{
                  icon: "refresh",
                  text: this.props.intl.formatMessage({
                    id: "GENERAL.BUTTON.REFRESH",
                    defaultMessage: "Refresh"
                  }),
                  onClick: this.loadData
                }}
              />
              <ToolbarItem name="exportButton" location="after" />
              <ToolbarItem name="columnChooserButton" location="after" />
              <ToolbarItem name="searchPanel" location="after" />
            </Toolbar>

          </DataGrid>
        </PortletBody>
      </Portlet>
    );
  }
}

export default injectIntl(withSnackbar(AISettings));